import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Save, ArrowLeft, Eye, FileText, EyeOff } from "lucide-react";
import ImageUploader from "../../components/admin/ImageUploader";

const EMPTY = {
    title: "",
    type: "blog",
    category: "",
    excerpt: "",
    content: "",
    cover_image: "",
    status: "draft",
    event_date: "",
    event_end: "",
    event_location: "",
    event_register_url: "",
    tags: [],
};

export default function PostEditor() {
    const { id } = useParams();
    const isNew = !id;
    const [form, setForm] = useState(EMPTY);
    const [tagsRaw, setTagsRaw] = useState("");
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(null); // null | "draft" | "publish" | "unpublish"
    const nav = useNavigate();

    useEffect(() => {
        if (isNew) return;
        (async () => {
            try {
                const { data } = await api.get(`/admin/posts/${id}`);
                setForm({
                    title: data.title || "",
                    type: data.type || "blog",
                    category: data.category || "",
                    excerpt: data.excerpt || "",
                    content: data.content || "",
                    cover_image: data.cover_image || "",
                    status: data.status || "draft",
                    event_date: data.event_date || "",
                    event_end: data.event_end || "",
                    event_location: data.event_location || "",
                    event_register_url: data.event_register_url || "",
                    tags: data.tags || [],
                });
                setTagsRaw((data.tags || []).join(", "));
            } catch {
                toast.error("Post not found");
                nav("/admin");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isNew, nav]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const doSave = async (actionStatus, actionLabel) => {
        if (!form.title.trim() || !form.content.trim()) {
            toast.error("Title and content are required");
            return;
        }
        setSaving(actionLabel);
        const payload = {
            ...form,
            status: actionStatus,
            category: form.category || null,
            cover_image: form.cover_image || null,
            event_date: form.event_date || null,
            event_end: form.event_end || null,
            event_location: form.event_location || null,
            event_register_url: form.event_register_url || null,
            tags: tagsRaw.split(",").map((t) => t.trim()).filter(Boolean),
        };
        try {
            if (isNew) {
                await api.post("/posts", payload);
            } else {
                await api.put(`/posts/${id}`, payload);
            }
            const msgs = {
                draft: isNew ? "Draft saved" : "Draft updated",
                publish: isNew ? "Post published" : "Changes published",
                unpublish: "Post unpublished (now a draft)",
            };
            toast.success(msgs[actionLabel]);
            nav("/admin");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(null);
        }
    };

    if (loading) return <div className="min-h-screen bg-[#050510] text-white p-12 tech-label">Loading…</div>;

    const isPublished = form.status === "published";

    return (
        <div className="min-h-screen bg-[#050510] text-white">
            <header className="glass-nav fixed top-0 inset-x-0 z-30 py-3">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between gap-4 flex-wrap">
                    <Link to="/admin" className="inline-flex items-center gap-2 tech-label hover:text-white">
                        <ArrowLeft size={14} /> Admin
                    </Link>
                    <div className="flex items-center gap-3">
                        <div className="font-serif-display text-xl">{isNew ? "New Post" : "Edit Post"}</div>
                        {!isNew && (
                            <span
                                className={`chip ${isPublished ? "chip-active" : ""}`}
                                data-testid="post-status-badge"
                            >
                                {isPublished ? "Published" : "Draft"}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {isPublished ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => doSave("draft", "unpublish")}
                                    disabled={!!saving}
                                    className="btn-secondary !py-2"
                                    data-testid="post-unpublish"
                                    title="Revert to draft — post disappears from the public site"
                                >
                                    <EyeOff size={14} /> {saving === "unpublish" ? "…" : "Unpublish"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => doSave("published", "publish")}
                                    disabled={!!saving}
                                    className="btn-primary !py-2"
                                    data-testid="post-save-changes"
                                >
                                    <Save size={14} /> {saving === "publish" ? "Saving…" : "Save changes"}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={() => doSave("draft", "draft")}
                                    disabled={!!saving}
                                    className="btn-secondary !py-2"
                                    data-testid="post-save-draft"
                                >
                                    <FileText size={14} /> {saving === "draft" ? "Saving…" : "Save draft"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => doSave("published", "publish")}
                                    disabled={!!saving}
                                    className="btn-primary !py-2"
                                    data-testid="post-publish"
                                >
                                    <Eye size={14} /> {saving === "publish" ? "Publishing…" : "Publish"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <form id="post-form" onSubmit={(e) => { e.preventDefault(); doSave(isPublished ? "published" : "draft", isPublished ? "publish" : "draft"); }} className="pt-28 pb-20 max-w-[1100px] mx-auto px-6 md:px-10 space-y-8">
                <div>
                    <label className="tech-label mb-2 block">Title *</label>
                    <input required className="tech-input" value={form.title} onChange={set("title")} data-testid="post-title" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="tech-label mb-2 block">Type *</label>
                        <select className="tech-input" value={form.type} onChange={set("type")} data-testid="post-type">
                            <option value="blog">Blog</option>
                            <option value="news">News</option>
                            <option value="event">Event</option>
                        </select>
                    </div>
                    <div>
                        <label className="tech-label mb-2 block">Category</label>
                        <input className="tech-input" value={form.category} onChange={set("category")} data-testid="post-category" />
                    </div>
                </div>

                {form.type === "event" && (
                    <div className="tech-card !p-6 space-y-4 border-[var(--accent-indigo)]/30">
                        <div className="overline">Event details</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="tech-label mb-2 block">Start (ISO)</label>
                                <input className="tech-input" value={form.event_date || ""} onChange={set("event_date")} placeholder="2026-06-18T09:00:00Z" data-testid="post-event-date" />
                            </div>
                            <div>
                                <label className="tech-label mb-2 block">End (ISO — optional)</label>
                                <input className="tech-input" value={form.event_end || ""} onChange={set("event_end")} placeholder="2026-06-18T11:00:00Z" data-testid="post-event-end" />
                            </div>
                        </div>
                        <div>
                            <label className="tech-label mb-2 block">Location</label>
                            <input className="tech-input" value={form.event_location || ""} onChange={set("event_location")} placeholder="IIT Madras · Chennai, India" data-testid="post-event-location" />
                        </div>
                        <div>
                            <label className="tech-label mb-2 block">Register / RSVP URL</label>
                            <input className="tech-input" value={form.event_register_url || ""} onChange={set("event_register_url")} placeholder="https://lu.ma/..." data-testid="post-event-register" />
                        </div>
                    </div>
                )}

                <div>
                    <label className="tech-label mb-2 block">Cover Image</label>
                    <ImageUploader
                        value={form.cover_image}
                        onChange={(v) => setForm((f) => ({ ...f, cover_image: v }))}
                        testIdPrefix="post-cover"
                    />
                </div>
                <div>
                    <label className="tech-label mb-2 block">Excerpt</label>
                    <textarea rows={3} className="tech-input" value={form.excerpt} onChange={set("excerpt")} data-testid="post-excerpt" />
                </div>
                <div>
                    <label className="tech-label mb-2 block">Content * <span className="text-white/40 normal-case tracking-normal">(supports # / ## / ### headings, **bold**, *italic*, lists)</span></label>
                    <textarea required rows={16} className="tech-input" value={form.content} onChange={set("content")} data-testid="post-content" />
                </div>
                <div>
                    <label className="tech-label mb-2 block">Tags (comma separated)</label>
                    <input className="tech-input" value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} data-testid="post-tags" />
                </div>
            </form>
        </div>
    );
}
